export type TelemetryConfig = {
  hookConsole: boolean;
  url: string;
  authToken?: () => Promise<string | undefined>;
  logToConsole?: boolean;
  sessionId: string;
  paused: boolean;
  delay: 2500;
};

export type TelemetryEvent = {
  eventName?: string;
  eventSource?: string;
  eventDescription?: string;
  eventData?: unknown;
};

const Telemetry = (() => {
  const config: TelemetryConfig = {
    hookConsole: false,
    url: '/api/zoe/telemetry/report-events',
    authToken: undefined,
    sessionId: Math.random().toString(36).substring(2),
    logToConsole: true,
    paused: true,
    delay: 2500,
  };

  const events: TelemetryEvent[] = [];

  const oldErrorHandler = console.error;

  let errors = 0;
  let hookedConsole = false;

  const process = async () => {
    const localEvents = [...events];
    if (localEvents.length === 0) {
      return;
    }

    // paused - so we're just dropping log data right now.
    if (config.paused) {
      events.length = 0;
      return;
    }

    if (!config.url) {
      (oldErrorHandler || console.error)('[telemetry] events to upload, but no URL!');
      return;
    }

    events.length = 0;

    const authToken = config.authToken ? await config.authToken() : undefined;

    try {
      const response = await fetch(config.url, {
        method: 'POST',
        headers: {
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
          'X-Mst-Session-ID': config.sessionId,
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
        body: JSON.stringify(
          localEvents.map(({ eventName, eventSource, eventDescription, eventData }) => {
            const trimmedClob = eventData ? JSON.stringify(eventData) : undefined;
            return {
              sessionId: config.sessionId,
              eventName,
              eventSource,
              eventDescription,
              eventData: trimmedClob && trimmedClob.length > 1024 ? `${trimmedClob.substring(0, 1024)}...` : trimmedClob,
            };
          })
        ),
      });

      if (!response.ok) {
        (oldErrorHandler || console.error)('[telemetry] did not get OK response...');
        errors++;
        events.push(...localEvents);
      } else {
        errors = 0;
      }
    } catch (error) {
      errors++;
      events.push(...localEvents);
      (oldErrorHandler || console.error)('[telemetry] failed to upload...', error);
    }

    if (errors > 5) {
      // shut down logging. we've failed.
      (oldErrorHandler || console.error)('[telemetry] received 5 errors; shutting down.');
      events.length = 0;
      config.paused = true;
    }
  };

  let id = setInterval(process, config.delay);

  const log = (o: TelemetryEvent) => {
    events.push(o);

    if (config.logToConsole) {
      console.debug('[telemetry] %s, %s, %s, %o', o.eventName, o.eventSource, o.eventDescription, o.eventData);
    }
  };

  const handleNewConfig = () => {
    if (!config.url) {
      clearInterval(id);
      console.error('[telemetry] URL is not set; telemetry has been stopped...');
      return;
    }

    if (config.hookConsole && !hookedConsole) {
      console.error = (...parameters) => {
        oldErrorHandler.call(console, ...parameters);
        events.push({
          eventName: 'UNHANDLED_EXCEPTION',
          eventSource: 'app',
          eventDescription: 'An error has been logged to the console!',
          eventData: JSON.stringify(parameters),
        });
      };
      hookedConsole = true;
    } else if (!config.hookConsole && hookedConsole) {
      console.error = oldErrorHandler;
      hookedConsole = false;
    }

    clearInterval(id);
    id = setInterval(process, config.delay);
  };

  const setConfig = (newConfig: Partial<TelemetryConfig>) => {
    Object.assign(config, newConfig);
    handleNewConfig();
  };

  return {
    log,
    setConfig,
    getConfig: () => ({ ...config }),
    dispose: () => clearInterval(id),
    getQueuedEvents: () => [...events],
    clear: () => {
      events.length = 0;
    },
    pause: () => setConfig({ paused: true }),
    resume: () => setConfig({ paused: false }),
  };
})();

export default Telemetry;
export { Telemetry };
